import {
    PAGINATION_PAGE_PARAM,
    PAGINATION_PER_PAGE_PARAM,
    SearchParamController,
} from '@clockout-dev/design-elements';
import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import SupportDelegate from './support.delegate';
import MultimediaController from './multimedia.controller';
import IssuesDelegate from './issues.delegate';
import IssuesController from './issues.controller';
import IssueStatusMapController from '../common/issueTracker/issueStatusMap.controller';
import IssueDetailController from '../common/issueTracker/issueDetail.controller';
import { SupportTicketConstants } from './support.enums';
import {
    ISSUES_PER_PAGE,
    ISSUE_STATUS_FILTER_ALL,
    ISSUE_VIEWS,
    ISSUE_VIEW_DEFAULT,
    SUPPORT_TABS,
    SUPPORT_TAB_DEFAULT,
} from './issueTracker.constants';

class SupportController extends Inherit(
    ClockoutController,
    MultimediaController,
    SupportDelegate,
    IssuesDelegate,
    IssuesController,
    IssueStatusMapController,
    IssueDetailController,
    SearchParamController,
) {
    state = {
        user: {},
        // NOT `true`: only the tab the URL opens on loads, so a standing
        // `true` here left the tickets table spinning forever whenever the
        // screen opened on ?tab=issues. `loadTicketsPage` sets it when it runs.
        ticketsLoading: false,
        // "has this tab ever fetched", which is NOT `tickets.length` — an FI with
        // genuinely zero tickets would otherwise refetch on every tab switch.
        ticketsLoaded: false,
        tickets: [],
        pageinfo: {},
        statusTab: SupportTicketConstants.LIST_STATUS_DEFAULT,
        listPageSize: SupportTicketConstants.LIST_PER_PAGE,
        createModalOpen: false,
        createSubmitting: false,
        newTicketSubject: '',
        newTicketBody: '',
        newTicketAttachments: [],
        newTicketCategory: null,
        newTicketPriority: null,
        pagination: null,

        // Jira tab (KAN-628). Prefixed so nothing here can collide with the
        // tickets tab's own list state above.
        supportTab: SUPPORT_TAB_DEFAULT,
        issuesLoading: false,
        issuesError: false,
        issuesLoaded: false,
        // Board columns, keyed by status: each owns its own page/total/hasNext
        // because each is a separate `?status=` query scrolled on its own.
        issueBoard: {},
        // One page of the TABLE. The board keeps its rows in `issueBoard`.
        issueRows: [],
        issuePageInfo: {},
        issuePagination: null,
        issuesPageSize: ISSUES_PER_PAGE,
        issueStatusFilter: ISSUE_STATUS_FILTER_ALL,
        issueView: ISSUE_VIEW_DEFAULT,
        issueStatusMap: null,
        issueSync: null,

        // The detail modal, fetched on open (the list rows are too slim to
        // render one from). Shared with the ticket page via IssueDetailController.
        issueDetailKey: null,
        issueDetail: null,
        issueDetailLoading: false,
        issueDetailError: false,
    };

    async initialize() {
        if (!this.user?.access?.length) {
            this.navigateLogin();
            return;
        }

        const { initialPage, initialPerPage } = this.readPaginationFromUrl();
        const initialStatus = this.readStatusTabFromUrl();
        const initialTab = this.readSupportTabFromUrl();
        const initialIssueView = this.readIssueViewFromUrl();
        // The board renders no chips, so a `?issueStatus=` arriving alongside it
        // would filter with nothing on screen to undo it. The table honours the
        // param; the board drops it.
        const initialIssueStatus = initialIssueView === ISSUE_VIEWS.TABLE
            ? this.readIssueStatusFromUrl()
            : ISSUE_STATUS_FILTER_ALL;

        this.setState(
            {
                user: this.data.user,
                listPageSize: initialPerPage,
                statusTab: initialStatus,
                supportTab: initialTab,
                issuesPageSize: initialPerPage,
                issueStatusFilter: initialIssueStatus,
                issueView: initialIssueView,
            },
            () => {
                // Only the tab the URL actually opens on fetches. The other one
                // loads on first switch, so landing on Support never costs a
                // Jira request and vice versa.
                if (initialTab === SUPPORT_TABS.ISSUES) {
                    this.loadIssuesForView(initialPage);
                } else {
                    this.loadTicketsPage(initialPage);
                }
            }
        );

        // Cached for the session by the data layer; safe to call on every mount.
        this.loadIssueStatusMap();
    }

    /**
     * Teardown. `_unmounted` is what the Jira loaders, the status-map loader and
     * the detail-modal fetch all check before their `setState`, and without this
     * hook every one of those guards was dead — the flag was read on this screen
     * and never written, so a slow Jira response landed on an unmounted screen.
     *
     * Same contract as `reports.controller.js#cleanup` and
     * `users/user/user.controller.js`, which is where the guards were copied from.
     */
    async cleanup() {
        this._unmounted = true;
    }

    readPaginationFromUrl() {
        const rawPage = parseInt(this.getSearchParamValue(PAGINATION_PAGE_PARAM), 10);
        const rawPerPage = parseInt(this.getSearchParamValue(PAGINATION_PER_PAGE_PARAM), 10);
        return {
            initialPage: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
            initialPerPage:
                Number.isFinite(rawPerPage) && rawPerPage > 0
                    ? rawPerPage
                    : SupportTicketConstants.LIST_PER_PAGE,
        };
    }

    readStatusTabFromUrl() {
        const raw = this.getSearchParamValue(SupportTicketConstants.LIST_STATUS_PARAM);
        const isValid = SupportTicketConstants.LIST_STATUS_TABS.some((t) => t.key === raw);
        return isValid ? raw : SupportTicketConstants.LIST_STATUS_DEFAULT;
    }

    async loadTicketsPage(page = 1) {
        this.setTicketsLoading(true);

        const { pageinfo, statusTab, listPageSize } = this.state;
        const perPage = listPageSize || pageinfo?.perPage || SupportTicketConstants.LIST_PER_PAGE;
        const filters = { page, perPage };
        if (statusTab && statusTab !== 'all') {
            filters.status = statusTab;
        }

        try {
            const data = await this.data.fetchTickets(filters);
            this.setPagination(data.items, data.pageinfo);
            this.setTicketsLoading(false);
        } catch (error) {
            console.error('Error loading tickets:', error);
            this.setTicketsLoaded();
            this.setTicketsLoading(false);
        }
    }

    async handleCreateSubmit() {
        const { newTicketSubject, newTicketBody, newTicketAttachments, newTicketCategory, newTicketPriority } = this.state;
        const subject = (newTicketSubject || '').trim();
        const body = (newTicketBody || '').trim();
        if (!subject || !body) {
            this.props.notificationApi.error({
                message: 'Missing fields',
                description: 'Subject and message are required.'
            });
            return;
        }
        if (subject.length > SupportTicketConstants.SUBJECT_MAX_LENGTH) {
            this.props.notificationApi.error({
                message: 'Subject too long',
                description: `Subject must be at most ${SupportTicketConstants.SUBJECT_MAX_LENGTH} characters.`
            });
            return;
        }
        if (body.length > SupportTicketConstants.BODY_MAX_LENGTH) {
            this.props.notificationApi.error({
                message: 'Message too long',
                description: `Message must be at most ${SupportTicketConstants.BODY_MAX_LENGTH} characters.`
            });
            return;
        }

        this.setCreateSubmitting(true);
        try {
            // Validate and upload attachments before creating ticket
            let files = [];
            if (newTicketAttachments.length > 0) {
                files = await this.uploadAttachments(newTicketAttachments, {
                    requestPresigned: this.data.requestPresignedTicketUrl.bind(this.data),
                    uploadToS3: this.data.uploadFileToS3.bind(this.data)
                });
            }

            await this.data.createTicket({ subject, body, category: newTicketCategory, priority: newTicketPriority }, files);
            this.props.notificationApi.success({
                message: 'Ticket created',
                description: 'Your support request was submitted.'
            });
            this.handleCloseCreateModal();
            await this.loadTicketsPage(1);
        } catch (error) {
            // Distinguish between upload and ticket creation failures
            if (error.code === 'UPLOAD_PARTIAL_FAILURE') {
                this.props.notificationApi.error({
                    message: 'File upload failed',
                    description: error.message + ' Please try again or contact support if files remain inaccessible.'
                });
            } else if (error.code === 'UPLOAD_FAILED') {
                this.props.notificationApi.error({
                    message: 'File upload failed',
                    description: error.message || 'Could not upload attachments. Please try again.'
                });
            } else {
                const desc = error?.response?.data?.error || error?.message || 'Could not create ticket. Please try again.';
                this.props.notificationApi.error({
                    message: 'Create ticket failed',
                    description: desc
                });
            }
        } finally {
            this.setCreateSubmitting(false);
        }
    }

    handleTableChange(pagination) {
        const nextSize = pagination?.pageSize ?? this.state.listPageSize;
        const sizeChanged = nextSize !== this.state.listPageSize;

        if (sizeChanged) {
            this.setPerPage(nextSize, () => this.loadTicketsPage(1));
            return
        }

        const nextPage = pagination?.current || 1;
        this.loadTicketsPage(nextPage);
    }

    handleStatusTabChange(value) {
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setStatusTab(value, () => this.loadTicketsPage(1));
    }

    handleRowClick(record) {
        if (record?.id) this.navigateTicket(record.id);
    }
}

export default SupportController;
